import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { AppModalCloseButton } from '../common/AppModalCloseButton';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { sampleService } from '../../services/sample.service';
import type { LocationShareTarget } from '../../utils/locationShare';
import { locationShareTargetKey } from '../../utils/locationShare';
import { rebasePublicShareUrl } from '../../utils/publicShareUrl';
import { translateAxiosErrorMessage } from '../../utils/error.utils';

export type SampleExternalShareModalProps = {
  show: boolean;
  onHide: () => void;
  adminViewAll?: boolean;
} & (
  | { mode?: 'sample'; sampleId: number | null; locationTarget?: never }
  | { mode: 'location'; locationTarget: LocationShareTarget | null; sampleId?: never }
);

const PUBLIC_URL_INPUT_ID = 'external-public-url';

type ShareUrlResult = {
  encryptedUrl: string;
  publicShareDisabled?: boolean;
  publicLocationShareDisabled?: boolean;
  storageRoomId?: number;
};

function selectPublicUrlInput() {
  const input = document.getElementById(PUBLIC_URL_INPUT_ID) as HTMLInputElement | null;
  input?.focus();
  input?.select();
  return input;
}

function locationShareModalTitle(
  scope: LocationShareTarget['scope'] | undefined,
  t: (key: string, opts?: { defaultValue: string }) => string
): string {
  if (scope === 'case') {
    return t('sample.location_share_modal_title_case', { defaultValue: 'ケースの共有URL' });
  }
  if (scope === 'drawer') {
    return t('sample.location_share_modal_title_drawer', {
      defaultValue: '引き出しの共有URL',
    });
  }
  if (scope === 'room') {
    return t('sample.location_share_modal_title_room', { defaultValue: '保管庫の共有URL' });
  }
  return t('sample.location_share_modal_title', { defaultValue: '場所の共有URL' });
}

/**
 * 共有URLモーダル。サンプル単体 / 保管庫·引き出し·ケース単位。
 */
export default function SampleExternalShareModal(props: SampleExternalShareModalProps) {
  const { show, onHide, adminViewAll } = props;
  const mode = props.mode ?? 'sample';
  const sampleId = mode === 'sample' ? props.sampleId : null;
  const locationTarget = mode === 'location' ? props.locationTarget : null;
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const {
    mutate: loadShareUrl,
    reset: resetShareUrl,
    isPending,
    isError,
    data,
  } = useMutation({
    mutationFn: async (): Promise<ShareUrlResult> => {
      if (mode === 'location') {
        if (!locationTarget) throw new Error('missing location');
        const res = await sampleService.generateLocationShareUrl(locationTarget, {
          adminViewAll: adminViewAll || undefined,
        });
        return {
          encryptedUrl: res.data.encryptedUrl,
          publicLocationShareDisabled: res.data.publicLocationShareDisabled ?? false,
          storageRoomId: res.data.storageRoomId,
        };
      }
      if (sampleId == null) throw new Error('missing sample');
      const res = await sampleService.generateEncryptedUrl(sampleId, {
        adminViewAll: adminViewAll || undefined,
      });
      return {
        encryptedUrl: res.data.encryptedUrl,
        publicShareDisabled: res.data.publicShareDisabled ?? false,
      };
    },
  });

  const {
    mutate: toggleShare,
    reset: resetToggleShare,
    isPending: isTogglePending,
    isError: isToggleError,
    error: toggleError,
  } = useMutation({
    mutationFn: async (disabled: boolean) => {
      if (mode === 'location' && locationTarget) {
        await sampleService.setPublicLocationShareDisabled(
          locationTarget.storageRoomId,
          disabled,
          { adminViewAll: adminViewAll || undefined }
        );
        return;
      }
      if (sampleId == null) throw new Error('missing sample');
      await sampleService.setPublicShareDisabled(sampleId, disabled, {
        adminViewAll: adminViewAll || undefined,
      });
    },
    onSuccess: () => loadShareUrl(),
  });

  const toggleShareErrorMessage = isToggleError
    ? translateAxiosErrorMessage(
        toggleError,
        t,
        t('sample.public_share_toggle_failed', {
          defaultValue: '共有の切り替えに失敗しました',
        })
      )
    : '';

  const locationKey = locationTarget ? locationShareTargetKey(locationTarget) : '';
  /** 初回取得のみフルスピナー。再開後の再取得では既存URLを残す */
  const showInitialLoad = isPending && !data;

  useEffect(() => {
    if (!show) {
      resetShareUrl();
      resetToggleShare();
      setCopied(false);
      return;
    }
    if (mode === 'sample' && sampleId == null) return;
    if (mode === 'location' && !locationKey) return;
    setCopied(false);
    resetToggleShare();
    loadShareUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadShareUrl/reset の identity 変動で copied を消さない
  }, [show, mode, sampleId, locationKey, adminViewAll]);

  const shareDisabled =
    mode === 'sample'
      ? (data?.publicShareDisabled ?? false)
      : (data?.publicLocationShareDisabled ?? false);

  const publicUrl = data?.encryptedUrl ? rebasePublicShareUrl(data.encryptedUrl) : '';
  const closeLabel = t('common.close');
  const closeTitle = t('sample.external_share_close_title', {
    defaultValue: '共有URLを閉じる',
  });
  const urlFieldLabel = t('sample.public_url');
  const urlFieldTitle = t('sample.public_url_field_title', {
    defaultValue: t('sample.public_url_note'),
  });

  const markCopied = () => {
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2000);
  };

  const copyViaSelection = (): boolean => {
    const input = selectPublicUrlInput();
    if (!input) return false;
    try {
      return document.execCommand('copy');
    } catch {
      return false;
    }
  };

  const handleCopy = async () => {
    if (!publicUrl) return;
    let ok = false;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(publicUrl);
        ok = true;
      }
    } catch {
      /* Clipboard API 拒否時は選択コピーへ */
    }
    if (!ok) {
      ok = copyViaSelection();
    }
    if (ok) {
      markCopied();
    } else {
      selectPublicUrlInput();
      setCopied(false);
    }
  };

  const handleHide = () => {
    resetShareUrl();
    resetToggleShare();
    setCopied(false);
    onHide();
  };

  const title = useMemo(() => {
    if (mode !== 'location') {
      return t('sample.external_share_modal_title', {
        defaultValue: 'サンプルの共有URL',
      });
    }
    return locationShareModalTitle(locationTarget?.scope, t);
  }, [mode, locationTarget?.scope, t]);

  const isLocation = mode === 'location';
  const intro = isLocation
    ? t('sample.location_share_intro', {
        defaultValue:
          'ログインなしで、この保管庫 · 引き出し · ケースのサンプル一覧を閲覧できます · 各サンプルの詳細 · 使用記録は一覧から開けます',
      })
    : t('sample.external_share_intro');

  const examplesTitle = isLocation
    ? t('sample.location_share_examples_title', { defaultValue: 'できることの例' })
    : t('sample.external_share_examples_title');

  const exampleItems = isLocation
    ? [
        t('sample.location_share_example_1', {
          defaultValue: '保管庫 · 引き出し · ケース単位で中身のサンプル一覧を確認する',
        }),
        t('sample.location_share_example_2', {
          defaultValue: '一覧から各サンプルの公開ページを開き、残量や使用記録を確認 · 登録する',
        }),
      ]
    : [
        t('sample.external_share_example_1'),
        t('sample.external_share_example_2'),
        t('sample.external_share_example_3'),
      ];

  const copyShort = copied
    ? t('sample.url_copied_short', { defaultValue: 'コピー済み' })
    : t('common.copy');
  const copyTitle = copied
    ? t('sample.url_copied')
    : t('sample.copy_url', { defaultValue: 'URLをコピー' });

  const revokeLabel = t('sample.public_share_revoke_button', {
    defaultValue: '共有を停止',
  });
  const enableLabel = t('sample.public_share_enable_button', {
    defaultValue: '共有を再開',
  });
  const revokeTitle = t('sample.public_share_revoke_title', {
    defaultValue: 'このURLでの閲覧 · 登録を停止する',
  });
  const enableTitle = t('sample.public_share_enable_title', {
    defaultValue: '停止した共有URLを再び有効にする',
  });

  return (
    <Modal
      dialogClassName="sample-system-dialog sample-external-share-dialog"
      show={show}
      onHide={handleHide}
      size="lg"
      fullscreen="sm-down"
      scrollable
    >
      <Modal.Header>
        <Modal.Title>
          <i className="fas fa-share-alt me-2" aria-hidden="true" />
          {title}
        </Modal.Title>
        <AppModalCloseButton onClick={handleHide} ariaLabel={closeLabel} title={closeTitle} />
      </Modal.Header>
      <Modal.Body>
        {showInitialLoad && (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" className="me-2" />
            <span>{t('sample.external_share_loading')}</span>
          </div>
        )}
        {isError && !showInitialLoad && (
          <Alert variant="danger" className="mb-0">
            {t('sample.external_share_load_failed', {
              defaultValue: '共有URLの取得に失敗しました',
            })}
          </Alert>
        )}
        {publicUrl && !showInitialLoad && (
          <>
            {shareDisabled && (
              <Alert variant="info" className="mb-3 small">
                {isLocation
                  ? t('sample.location_public_share_disabled_notice', {
                      defaultValue:
                        '共有は停止中です · このURLを開いても一覧は表示されません · 「共有を再開」で同じURLを再度有効にできます',
                    })
                  : t('sample.public_share_disabled_notice', {
                      defaultValue:
                        '共有は停止中です · このURLを開いてもサンプルは表示されません · 「共有を再開」で同じURLを再度有効にできます',
                    })}
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label htmlFor={PUBLIC_URL_INPUT_ID} className="visually-hidden">
                {urlFieldLabel}
              </Form.Label>
              <div className="sample-external-share-url-row">
                <Form.Control
                  id={PUBLIC_URL_INPUT_ID}
                  type="text"
                  value={publicUrl}
                  readOnly
                  aria-label={urlFieldLabel}
                  title={urlFieldTitle}
                  onClick={(e) => (e.target as HTMLInputElement).select()}
                  autoComplete="off"
                />
                <Button
                  variant={copied ? 'success' : 'primary'}
                  onClick={() => void handleCopy()}
                  aria-live="polite"
                  aria-label={copyShort}
                  title={copyTitle}
                  className="sample-external-share-copy-btn"
                  disabled={shareDisabled}
                >
                  <i
                    className={`fas ${copied ? 'fa-check' : 'fa-copy'} me-1`}
                    aria-hidden="true"
                  />
                  <span className="sample-external-share-copy-label">{copyShort}</span>
                </Button>
              </div>
              <Form.Text className="text-muted small" aria-live="polite">
                {copied
                  ? t('sample.public_url_note_copied', {
                      defaultValue:
                        'クリップボードにコピーしました · 必要ならもう一度コピーできます',
                    })
                  : t('sample.public_url_note')}
              </Form.Text>
            </Form.Group>

            <div className="d-flex flex-wrap gap-2 mb-3">
              {toggleShareErrorMessage ? (
                <Alert variant="danger" className="mb-0 small py-2 flex-grow-1">
                  {toggleShareErrorMessage}
                </Alert>
              ) : null}
              {shareDisabled ? (
                <Button
                  variant="outline-primary"
                  size="sm"
                  disabled={isTogglePending}
                  onClick={() => toggleShare(false)}
                  title={enableTitle}
                  aria-label={enableLabel}
                >
                  {isTogglePending ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <i className="fas fa-link me-1" aria-hidden="true" />
                  )}
                  {enableLabel}
                </Button>
              ) : (
                <Button
                  variant="outline-danger"
                  size="sm"
                  disabled={isTogglePending}
                  onClick={() => toggleShare(true)}
                  title={revokeTitle}
                  aria-label={revokeLabel}
                >
                  {isTogglePending ? (
                    <Spinner animation="border" size="sm" className="me-1" />
                  ) : (
                    <i className="fas fa-ban me-1" aria-hidden="true" />
                  )}
                  {revokeLabel}
                </Button>
              )}
            </div>

            <p className="small text-muted mb-2">{intro}</p>

            <details className="sample-external-share-details mb-3">
              <summary
                className="small fw-semibold"
                title={t('sample.external_share_examples_title_hint', {
                  defaultValue: examplesTitle,
                })}
              >
                {examplesTitle}
              </summary>
              <ul className="small mb-0 mt-2 ps-3">
                {exampleItems.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </details>

            <Alert variant="warning" className="mb-0 small">
              <div className="d-flex align-items-start gap-2">
                <i className="fas fa-exclamation-triangle mt-1 flex-shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <strong className="d-block mb-1">{t('sample.security_warning')}</strong>
                  <p className="mb-1">{t('sample.public_url_security_note')}</p>
                  <p className="mb-0">{t('sample.public_url_security_note2')}</p>
                </div>
              </div>
            </Alert>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="sample-external-share-footer">
        <Button
          variant="outline-secondary"
          size="sm"
          className="sample-external-share-footer-close"
          onClick={handleHide}
          aria-label={closeLabel}
          title={closeTitle}
        >
          {closeLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
